import {
    Box,
    Typography,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Grid,
    Paper,
    TextField,
    Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { goApi } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';
import { useNotify } from 'react-admin';

const SectionQuestionEdit = ({ sectionId, question, onClose }: any) => {
    const notify = useNotify();

    const [questionData, setQuestionData] = useState({
        positiveMarks: '' as string | number,
        negativeMarks: '' as string | number,
        displayOrder: '' as string | number,
    });

    useEffect(() => {
        if (question) {
            setQuestionData({
                positiveMarks: question.positiveMarks ?? '',
                negativeMarks: question.negativeMarks ?? '',
                displayOrder: question.displayOrder ?? '',
            });
        }
    }, [question]);

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuestionData(prev => ({
            ...prev,
            [field]: value === '' ? '' : parseFloat(value),
        }));
    };

    const handleSubmit = async () => {
        const dataToSubmit = {
            positiveMarks:
                questionData.positiveMarks === '' ? 0 : Number(questionData.positiveMarks),
            negativeMarks:
                questionData.negativeMarks === '' ? 0 : Number(questionData.negativeMarks),
            displayOrder: questionData.displayOrder === '' ? 0 : Number(questionData.displayOrder),
        };
        try {
            await goApi.put(
                `/sections-question-mappings/${sectionId}/question/${question?.id}`,
                dataToSubmit,
            );
            notify('Question updated successfully', { type: 'success' });
            onClose();
        } catch (err: any) {
            notify(err?.message || 'Failed to update question', { type: 'error' });
        }
    };

    return (
        <>
            <DialogTitle
                sx={{
                    m: 0,
                    p: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6" component="span">
                    Question Details
                </Typography>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#fff3cd',
                    borderBottom: '1px solid #ffc107',
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Note: To edit content and options, use "Edit Question" button
                </Typography>
            </Box>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#f9f9f9',
                        }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: question?.content || '' }} />
                        {question?.image && (
                            <Box component="img" src={question.image} alt="Question" />
                        )}
                    </Box>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {question?.options?.map((option: any, index: number) => (
                        <Grid item xs={6} key={option.id}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: option.isCorrect ? '#c8e6c9' : '#fff',
                                    '&:hover': {
                                        backgroundColor: option.isCorrect ? '#a5d6a7' : '#f5f5f5',
                                    },
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>{index + 1}.</strong>{' '}
                                    <span dangerouslySetInnerHTML={{ __html: option.content }} />
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                        <TextField
                            label="Positive Marks"
                            type="number"
                            fullWidth
                            size="small"
                            value={questionData.positiveMarks}
                            onChange={handleChange('positiveMarks')}
                            InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Negative Marks"
                            type="number"
                            fullWidth
                            size="small"
                            value={questionData.negativeMarks}
                            onChange={handleChange('negativeMarks')}
                            InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Display Order"
                            type="number"
                            fullWidth
                            size="small"
                            value={questionData.displayOrder}
                            onChange={handleChange('displayOrder')}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                        />
                    </Grid>
                </Grid>

                {question?.solutionDescription && (
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Solution:
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: '#e3f2fd',
                                borderRadius: 1,
                                border: '1px solid #bbdefb',
                            }}
                        >
                            <div
                                dangerouslySetInnerHTML={{ __html: question.solutionDescription }}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 100 }}
                >
                    Submit
                </Button>
            </DialogActions>
        </>
    );
};

export default SectionQuestionEdit;
